import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ProjectSchema, UsageCostBreakdown } from '../types';
import { fetchProjectSchema } from '../hooks/useSchema';

type AppStep = 'project_selection' | 'questionnaire' | 'calculator' | 'results';

interface CalculatorState {
  step: AppStep;
  projectId: string | null;
  schema: ProjectSchema | null;
  isLoadingSchema: boolean;
  schemaError: string | null;

  answers: Record<string, unknown> | null;
  params: Record<string, unknown>;
  breakdown: UsageCostBreakdown | null;
  hints: Record<string, unknown>;
  isCalculating: boolean;
  configName: string;
  sidebarOpen: boolean;

  setStep: (step: AppStep) => void;
  selectProject: (projectId: string) => Promise<void>;
  setAnswers: (answers: Record<string, unknown>) => void;
  setParams: (params: Record<string, unknown>) => void;
  mergeParams: (partial: Record<string, unknown>) => void;
  setBreakdown: (b: UsageCostBreakdown | null) => void;
  setHints: (h: Record<string, unknown>) => void;
  setIsCalculating: (v: boolean) => void;
  setConfigName: (n: string) => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;

  reset: () => void;
  resetToQuestionnaire: () => void;
  loadConfig: (params: Record<string, unknown>, answers: Record<string, unknown>) => void;
}

export const useCalculatorStore = create<CalculatorState>()(
  persist(
    (set) => ({
      step: 'project_selection',
      projectId: null,
      schema: null,
      isLoadingSchema: false,
      schemaError: null,
      answers: null,
      params: {},
      breakdown: null,
      hints: {},
      isCalculating: false,
      configName: '',
      sidebarOpen: true,

      setStep: (step) => set({ step }),

      selectProject: async (projectId) => {
        set({ isLoadingSchema: true, schemaError: null, projectId });
        try {
          const schema = await fetchProjectSchema(projectId);
          set({
            schema,
            params: { ...schema.questionnaire.defaults },
            isLoadingSchema: false,
            step: 'questionnaire',
          });
        } catch (e) {
          set({
            schemaError: e instanceof Error ? e.message : 'Failed to load schema',
            isLoadingSchema: false,
          });
        }
      },

      setAnswers: (answers) => set({ answers }),
      setParams: (params) => set({ params }),
      mergeParams: (partial) => set((s) => ({ params: { ...s.params, ...partial } })),
      setBreakdown: (breakdown) => set({ breakdown }),
      setHints: (hints) => set({ hints }),
      setIsCalculating: (isCalculating) => set({ isCalculating }),
      setConfigName: (configName) => set({ configName }),
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
      toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),

      reset: () =>
        set({
          step: 'project_selection',
          projectId: null,
          schema: null,
          answers: null,
          params: {},
          breakdown: null,
          hints: {},
          configName: '',
          schemaError: null,
        }),

      resetToQuestionnaire: () =>
        set({
          step: 'questionnaire',
          answers: null,
          breakdown: null,
          hints: {},
          configName: '',
        }),

      loadConfig: (params, answers) =>
        set({
          params,
          answers,
          step: 'calculator',
        }),
    }),
    {
      name: 'cost-calculator-storage',
      partialize: (s) => ({
        projectId: s.projectId,
        answers: s.answers,
        params: s.params,
        configName: s.configName,
        sidebarOpen: s.sidebarOpen,
      }),
    }
  )
);
